<script lang="ts" setup>
import { type AccordionRootProps } from 'radix-vue'

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@toni/brand-ui'
import { ProductCardWithPurchaseOptions } from '@toni/commerce-api-types'

import ReviewHeading from '../ReviewHeading.vue'
import { useClient } from '../../utils/is-client'
import ProductRatings from '../ProductRatings.vue'

defineProps<{
  productCards: ProductCardWithPurchaseOptions[]
  accordionOpenId: AccordionRootProps['modelValue']
}>()

const { isClient } = useClient()

const emit = defineEmits(['update-accordion-open-id'])
</script>

<template>
  <Accordion v-if="isClient"
             :model-value="accordionOpenId"
             type="single"
             class="clear-both w-full"
             @update:model-value="emit('update-accordion-open-id', $event)">
    <AccordionItem value="desc">
      <AccordionTrigger class="!text-lg">
        <span class="text-heading-xs">{{ $t('commerce.review') }}</span>
      </AccordionTrigger>
      <AccordionContent>
        <div class="flex gap-5 mt-[0.4375rem] mb-4">
          <div v-for="product in productCards"
               :key="product.bauerProductCard.bauerProductCardId"
               class="flex flex-col min-h-full max-w-[11.875rem] gap-1.5"
               :class="productCards.length > 2 ? 'basis-1/4' : 'basis-1/2'">
            <ProductRatings v-if="product.bauerProductCard.ratings"
                            class="flex flex-col gap-1 relative w-full"
                            :ratings="
                              product.bauerProductCard.ratings?.map((rating) => ({
                                label: rating.title,
                                value: rating.value,
                                total: rating.maxValue,
                                style: rating.style
                              }))
                            "
                            :is-comparison="true" />
          </div>
        </div>

        <ReviewHeading
          type="pros"
          :title="productCards[0]?.bauerProductCard?.pros?.title || $t(`commerce.pros`)"
          variant="column" />
        <div class="flex gap-5 mt-[0.4375rem] mb-4">
          <div v-for="product in productCards"
               :key="product.bauerProductCard.bauerProductCardId"
               class="flex flex-col min-h-full border-b pb-4 max-w-[11.875rem] gap-1.5"
               :class="productCards.length > 2 ? 'basis-1/4' : 'basis-1/2'">
            <div
              v-for="(reason, index) in product.bauerProductCard.pros?.values || []"
              :key="index"
              class="flex flex-col gap-1">
              <h5
                v-if="reason.title"
                class="font-heading font-medium tracking-normal text-xs">
                {{ reason.title }}
              </h5>
              <p class="font-ui text-xs text-foreground whitespace-pre-wrap">
                {{ reason.description }}
              </p>
            </div>
          </div>
        </div>
        <ReviewHeading
          type="cons"
          :title="productCards[0]?.bauerProductCard?.cons?.title || $t(`commerce.pros`)"
          variant="column" />
        <div class="flex gap-5 mt-[0.4375rem] mb-4">
          <div v-for="product in productCards"
               :key="product.bauerProductCard.bauerProductCardId"
               class="flex flex-col min-h-full pb-4 max-w-[11.875rem] gap-1.5"
               :class="productCards.length > 2 ? 'basis-1/4' : 'basis-1/2'">
            <div
              v-for="(reason, index) in product.bauerProductCard.cons?.values || []"
              :key="index"
              class="flex flex-col gap-1">
              <h5
                v-if="reason.title"
                class="font-heading font-medium tracking-normal text-xs">
                {{ reason.title }}
              </h5>
              <p class="font-ui text-xs text-foreground whitespace-pre-wrap">
                {{ reason.description }}
              </p>
            </div>
          </div>
        </div>
      </AccordionContent>
    </AccordionItem>
  </Accordion>
</template>
